import { Chromosome } from '../types/algorithm';
import { calculateChromosomeFitness } from '../utils/fitnessCalculator';
import {
  initializePopulation,
  calculateRWP,
  rouletteWheelSelection,
  singlePointCrossover,
  mutate,
  findBestChromosome,
} from './geneticAlgorithm';

/**
 * Cultural Algorithm implementation from scratch
 * Extends GA with belief space (elite-based selection for crossover)
 */

/**
 * Elite crossover - uses top performers for crossover operations
 */
function eliteCrossover(
  population: Chromosome[],
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  eliteCount: number,
  crossoverCount: number
): void {
  // Sort population by fitness (descending)
  const sorted = [...population].sort((a, b) => b.fitness - a.fitness);
  const elite = sorted.slice(0, eliteCount);

  // Perform crossovers using elite selection
  for (let i = 0; i < crossoverCount; i += 2) {
    // Select parents from elite (can use roulette wheel within elite)
    calculateRWP(elite);
    const parent1Idx = rouletteWheelSelection(elite);
    const parent2Idx = rouletteWheelSelection(elite);

    const [child1, child2] = singlePointCrossover(
      elite[parent1Idx],
      elite[parent2Idx],
      taskCount
    );

    // Evaluate offspring
    calculateChromosomeFitness(child1, matrix, taskCount, serviceCount);
    calculateChromosomeFitness(child2, matrix, taskCount, serviceCount);

    // Replace worst individuals if offspring are better
    // Find worst indices by sorting current population
    const worstIndices = population
      .map((chrom, idx) => ({ chrom, idx }))
      .sort((a, b) => a.chrom.fitness - b.chrom.fitness)
      .map(item => item.idx);

    const worst1Idx = worstIndices[0];
    const worst2Idx = worstIndices.length > 1 ? worstIndices[1] : worst1Idx;

    if (worst1Idx !== undefined && child1.fitness > population[worst1Idx].fitness) {
      population[worst1Idx] = child1;
    }
    if (worst2Idx !== undefined && worst2Idx !== worst1Idx && i + 1 < crossoverCount && child2.fitness > population[worst2Idx].fitness) {
      population[worst2Idx] = child2;
    }
  }
}

/**
 * Main Cultural Algorithm evolution step
 */
export function evolveCultural(
  population: Chromosome[],
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  selectionRate: number,
  crossoverRate: number,
  mutationRate: number,
  eliteCount: number
): void {
  const popSize = population.length;
  const crossoverCount = Math.floor((popSize * crossoverRate) / 100);
  const mutateCount = Math.floor((popSize * mutationRate) / 100);

  // Elite-based crossover (belief space influence)
  eliteCrossover(population, taskCount, serviceCount, matrix, eliteCount, crossoverCount);

  // Mutation operations (same as GA)
  for (let i = 0; i < mutateCount; i++) {
    const idx = Math.floor(Math.random() * popSize);
    const mutated = mutate(population[idx], taskCount, serviceCount);
    calculateChromosomeFitness(mutated, matrix, taskCount, serviceCount);

    if (mutated.fitness > population[idx].fitness) {
      population[idx] = mutated;
    }
  }
}

/**
 * Runs Cultural Algorithm for specified generations
 */
export async function runCulturalAlgorithm(
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  parameters: {
    populationSize: number;
    generations: number;
    selectionRate: number;
    crossoverRate: number;
    mutationRate: number;
  },
  onGenerationComplete?: (generation: number, bestFitness: number) => void
): Promise<Chromosome> {
  // Initialize population
  let population = initializePopulation(
    parameters.populationSize,
    taskCount,
    serviceCount
  );

  // Evaluate initial population
  for (const chrom of population) {
    calculateChromosomeFitness(chrom, matrix, taskCount, serviceCount);
  }

  // Elite count (typically 10% of population)
  const eliteCount = Math.max(1, Math.floor(parameters.populationSize * 0.1));

  // Evolution loop
  for (let gen = 0; gen < parameters.generations; gen++) {
    evolveCultural(
      population,
      taskCount,
      serviceCount,
      matrix,
      parameters.selectionRate,
      parameters.crossoverRate,
      parameters.mutationRate,
      eliteCount
    );

    // Report progress
    const best = findBestChromosome(population);
    if (onGenerationComplete) {
      onGenerationComplete(gen + 1, best.fitness);
    }

    // Yield control to allow UI updates
    await new Promise(resolve => setTimeout(resolve, 0));
  }

  return findBestChromosome(population);
}

